import React, { useEffect, useRef } from 'react';

// One extra heart, after the profile picture's hearts: it grows in Togepi's hand, then floats up on a string and
// stays there like a balloon, bobbing and swaying. Drawn every frame (the string is a curve between the hand and the
// heart, which CSS can't animate everywhere). Placed in the avatar's own 80×80 box: (0, 0) is its top-left corner.

const SIZE = 20;                  // the heart, in px
const BOX = SIZE * 1.5;           // its box: roomy, so the emoji (drawn a little wider than its size) sits centred
const TIP = 0.4;                  // the heart's bottom tip, below its centre (× its size); the string ends just
                                  // inside it, under the heart, so it always meets the tip whatever the emoji font
const HAND = { x: 17, y: 43 };    // Togepi's hand, in the picture: the heart appears here, then floats up from it
const REST = { x: 4, y: -24 };    // where the balloon floats: up and to the left, clear of the thought bubble
const APPEAR = 0.6;               // seconds to grow in his hand, from tiny
const RISE = 1.4;                 // seconds to float up to REST

const ease = (p) => 1 - (1 - p) ** 3;
const pop = (p) => 1 + 2.7 * (p - 1) ** 3 + 1.7 * (p - 1) ** 2; // grows a touch past full size, then settles
const lerp = (a, b, p) => a + (b - a) * p;

export default function BalloonHeart({ delay = 600 }) {
  const heartRef = useRef(null);
  const stringRef = useRef(null);

  useEffect(() => {
    const heart = heartRef.current;
    const string = stringRef.current;
    if (!heart || !string) return undefined;

    // The heart at (x, y), turned by rot degrees; the string from the hand to its bottom tip (or hidden)
    const place = (x, y, scale, rot, opacity, sway) => {
      heart.style.transform = `translate(${x - BOX / 2}px, ${y - BOX / 2}px) rotate(${rot}deg) scale(${scale})`;
      heart.style.opacity = opacity;
      if (sway === null) { string.setAttribute('d', ''); return; }
      const r = SIZE * TIP * scale;
      const tipX = x - Math.sin((rot * Math.PI) / 180) * r;
      const tipY = y + Math.cos((rot * Math.PI) / 180) * r;
      const midX = (HAND.x + tipX) / 2 + sway;
      const midY = (HAND.y + tipY) / 2;
      string.setAttribute('d', `M ${HAND.x} ${HAND.y} Q ${midX} ${midY} ${tipX} ${tipY}`);
    };

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      place(REST.x, REST.y, 1, 0, 1, 0); // already held, standing still
      return undefined;
    }

    // In his hand: the heart sits on it, its tip just touching (so the string starts from nothing as it rises)
    const inHand = (scale) => HAND.y - SIZE * TIP * scale;
    let frame;
    const begin = performance.now() + delay;
    const tick = (now) => {
      const t = (now - begin) / 1000;
      if (t < 0) {
        place(HAND.x, inHand(0.1), 0.1, 0, 0, null);
      } else if (t < APPEAR) {
        const scale = lerp(0.1, 0.85, pop(t / APPEAR)); // growing in his hand, from tiny
        place(HAND.x, inHand(scale), scale, 0, Math.min(1, t * 6), null);
      } else if (t < APPEAR + RISE) {
        const p = ease((t - APPEAR) / RISE); // floating up, the string paying out behind it
        place(lerp(HAND.x, REST.x, p), lerp(inHand(0.85), REST.y, p), lerp(0.85, 1, p), 0, 1, 0);
      } else {
        const s = t - APPEAR - RISE; // held: bobbing up and down, drifting side to side, gently tilting
        place(REST.x + Math.sin(s * 0.8) * 5, REST.y + Math.sin(s * 1.25) * 3, 1,
          Math.sin(s * 0.8) * 10, 1, Math.sin(s * 1.1) * 4);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [delay]);

  return (
    <>
      <svg width="80" height="80" aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none', zIndex: 23 }}>
        <path ref={stringRef} fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1" strokeLinecap="round" />
      </svg>
      <span ref={heartRef} aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 0, width: BOX, height: BOX, fontSize: SIZE, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, pointerEvents: 'none', zIndex: 24,
          willChange: 'transform',
          filter: 'drop-shadow(0 0 3px rgba(251,113,133,0.6))' }}>💗</span>
    </>
  );
}
